import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/feature/SectionHeading";
import ShareModal from "./components/ShareModal";
import useSeo from "@/hooks/useSeo";
import useFavorites, { getHistory, recordHistory } from "@/hooks/useFavorites";
import type { Article } from "@/types/news";
import {
  formatDate,
  formatViews,
  getArticleById,
  getCategory,
  getCategoryName,
  getHotArticles,
  getRelatedArticles,
} from "@/utils/articles";

export default function DetailPage() {
  const { id } = useParams();
  const article = getArticleById(id);
  const category = article ? getCategory(article.category) : undefined;

  useSeo({
    title: article ? `${article.title} - 51黑料网详情` : "爆料不存在 - 51黑料网",
    description: article
      ? `${article.summary} 来自51黑料网${getCategoryName(article.category)}栏目，作者${article.author}。`
      : "该爆料内容已下架或不存在，请返回51黑料网首页浏览其他爆料资讯。",
    keywords: article
      ? `${article.tags.join(",")},${getCategoryName(article.category)},51黑料网`
      : "51黑料网,爆料详情",
  });

  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState("");
  const [historyArticles, setHistoryArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (!article) return;
    recordHistory(article.id);
    const historyItems = getHistory()
      .map((itemId) => getArticleById(itemId))
      .filter((item): item is Article => Boolean(item))
      .slice(0, 4);
    setHistoryArticles(historyItems);
  }, [article]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(100, Math.max(0, (scrollTop / height) * 100)) : 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center">
          <div className="max-w-portal mx-auto px-4 md:px-6 py-16 md:py-24 w-full text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl" aria-hidden="true" />
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              该爆料内容已下架或不存在
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              可能已被编辑部撤下，或者链接输入有误。你可以从下面的热门爆料继续浏览。
            </p>
            <div className="mt-7 max-w-2xl mx-auto text-left">
              {getHotArticles(3).map((item) => (
                <NewsCard key={item.id} article={item} variant="mini" />
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2">
              <Link
                to="/"
                className="w-full sm:w-auto h-10 px-5 inline-flex items-center justify-center rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/search"
                className="w-full sm:w-auto h-10 px-5 inline-flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                搜索其他爆料
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, 4);
  const hotSidebar = getHotArticles(6)
    .filter((item) => item.id !== article.id)
    .slice(0, 4);
  const favorite = isFavorite(article.id);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const [leadParagraph, ...restParagraphs] = article.paragraphs;

  const handleFavorite = () => {
    const added = toggleFavorite(article.id);
    setToast(added ? "已加入收藏 · 本地演示，未接入后台" : "已取消收藏 · 本地演示");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <div className="fixed top-0 left-0 z-[60] h-0.5 bg-accent-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      <SiteHeader />

      <main className="flex-1">
        <div className="max-w-portal mx-auto px-4 md:px-6">
          <nav className="py-4 flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
              {getCategoryName(article.category)}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-700 line-clamp-1">正文</span>
          </nav>

          <article>
            <header>
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-1 rounded-md bg-primary-600 text-background-50 text-[11px] font-medium hover:bg-primary-700 transition-colors"
                >
                  {getCategoryName(article.category)}
                </Link>
                <span className="px-2 py-1 rounded-md bg-secondary-100 text-secondary-800 text-[11px]">
                  {article.region}
                </span>
                {article.featured && (
                  <span className="px-2 py-1 rounded-md bg-accent-100 text-accent-900 text-[11px] font-medium">
                    独家爆料
                  </span>
                )}
              </div>

              <h1 className="mt-3 font-heading text-2xl md:text-4xl font-black leading-[1.3] text-foreground-950">
                {article.title}
              </h1>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                {article.summary}
              </p>

              <div className="mt-4 pb-4 border-b border-background-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-foreground-700">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-800 text-[11px] font-medium">
                      {article.author.slice(0, 1)}
                    </span>
                    {article.author}
                  </span>
                  <span className="w-px h-3 bg-background-300" />
                  <span className="font-label">{formatDate(article.publishedAt)}</span>
                  <span className="w-px h-3 bg-background-300" />
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-eye-line" aria-hidden="true" />
                    </span>
                    {formatViews(article.views)} 阅读
                  </span>
                  <span className="w-px h-3 bg-background-300" />
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-chat-3-line" aria-hidden="true" />
                    </span>
                    {article.commentCount} 条讨论
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      favorite
                        ? "bg-accent-100 border-accent-300 text-accent-900"
                        : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${favorite ? "ri-bookmark-fill" : "ri-bookmark-line"}`} aria-hidden="true" />
                    </span>
                    {favorite ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-share-forward-line" aria-hidden="true" />
                    </span>
                    分享
                  </button>
                </div>
              </div>
            </header>

            <figure className="mt-5">
              <div className="w-full aspect-[16/9] overflow-hidden rounded-lg bg-background-200">
                <img
                  src={article.image}
                  alt={`${article.title}配图`}
                  title={`${article.title} - 51黑料网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-[11px] text-foreground-400">
                示意图 · 图片由 AI 生成，与现实中的人物、场景无关
              </figcaption>
            </figure>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
              <div className="min-w-0">
                <div className="prose-article">
                  <p className="!text-[17px] md:!text-[18px] !font-medium !text-foreground-900 !leading-[1.9]">
                    {leadParagraph}
                  </p>
                  {restParagraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 18)}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-1">相关标签</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-5 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 flex items-start gap-2.5">
                  <span className="shrink-0 w-4 h-4 flex items-center justify-center text-accent-700 mt-0.5">
                    <i className="ri-information-line" aria-hidden="true" />
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-600">
                    本文为产品演示内容，人物、事件与图片均为虚构。收藏与浏览记录仅保存在浏览器本地，未接入后台。
                  </p>
                </div>

                <div className="mt-6 pt-5 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-foreground-600">
                    觉得这条线索有价值？分享前先看看相关背景。
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-share-forward-line" aria-hidden="true" />
                      </span>
                      分享给朋友
                    </button>
                    <Link
                      to={`/category/${article.category}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-secondary-100 text-sm text-foreground-800 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      更多{getCategoryName(article.category)}
                    </Link>
                  </div>
                </div>

                <section className="mt-8">
                  <SectionHeading title="相关爆料推荐" eyebrow="RELATED" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {related.map((item) => (
                      <NewsCard key={item.id} article={item} />
                    ))}
                  </div>
                </section>
              </div>

              <aside className="space-y-5">
                {category && (
                  <div className="rounded-lg bg-primary-700 text-background-50 p-5">
                    <p className="text-[11px] font-label tracking-[0.2em] text-accent-300">所属栏目</p>
                    <h2 className="mt-1 font-heading text-xl font-black">{category.name}</h2>
                    <p className="mt-2 text-xs leading-relaxed text-background-50/80">
                      {category.description}
                    </p>
                    <Link
                      to={`/category/${category.slug}`}
                      className="mt-3 inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 transition-colors cursor-pointer"
                    >
                      进入栏目
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-line" aria-hidden="true" />
                      </span>
                    </Link>
                  </div>
                )}

                <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                  <div className="flex items-center gap-2 pb-2 border-b border-background-200">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                      <i className="ri-fire-line text-base" aria-hidden="true" />
                    </span>
                    <h2 className="font-heading text-base font-bold text-foreground-950">热门推荐</h2>
                  </div>
                  <div className="divide-y divide-background-100">
                    {hotSidebar.map((item) => (
                      <NewsCard key={item.id} article={item} variant="mini" />
                    ))}
                  </div>
                </section>

                <section className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                      <i className="ri-history-line text-base" aria-hidden="true" />
                    </span>
                    <h2 className="font-heading text-sm font-bold text-foreground-950">最近浏览</h2>
                  </div>
                  {historyArticles.length > 0 ? (
                    <div className="divide-y divide-background-200/70">
                      {historyArticles.map((item) => (
                        <NewsCard key={item.id} article={item} variant="mini" />
                      ))}
                    </div>
                  ) : (
                    <p className="py-2 text-xs text-foreground-500">
                      还没有浏览记录，读完的爆料会出现在这里。
                    </p>
                  )}
                  <p className="mt-2 pt-2 border-t border-background-200 text-[10px] text-foreground-400">
                    浏览记录保存在本地，仅用于演示
                  </p>
                </section>
              </aside>
            </div>
          </article>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={article.title}
        url={shareUrl}
      />

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}